import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { auth, firestore } from '@tabletuck/firebase-services';
import { setCart, setUser } from '@tabletuck/redux-slices';
import { startTransition } from 'react';

/**
 * Normalize modifiers/customization into a flat array of { id, quantity }
 */
const normalizeOptions = (it) => {
  if (!it) return [];

  // If modifiers is an array (API lineItem shape)
  if (Array.isArray(it.modifiers)) {
    return it.modifiers.map((m) => ({
      id: m.catalogObjectId || m.uid || m.id || m.optionId || m.name || '',
      quantity: parseInt(m.quantity) || parseInt(m.qty) || 1,
    }));
  }

  // If customization is a map of groups -> array (bag item shape)
  if (it.customization && typeof it.customization === 'object') {
    const res = [];
    Object.values(it.customization).forEach((arr) => {
      if (Array.isArray(arr)) {
        arr.forEach((opt) => {
          res.push({
            id:
              opt.id ||
              opt.catalogObjectId ||
              opt.uid ||
              opt.optionId ||
              opt.modifierId ||
              opt.optionTitle ||
              opt.name ||
              '',
            quantity: parseInt(opt.quantity) || parseInt(opt.qty) || 1,
          });
        });
      }
    });
    return res;
  }

  // Fallback: maybe bag item has modifiers as map
  if (it.modifierDetails && Array.isArray(it.modifierDetails)) {
    return it.modifierDetails.map((m) => ({
      id: m.id || m.catalogObjectId || m.uid || m.optionId || m.name || '',
      quantity: parseInt(m.quantity) || 1,
    }));
  }

  return [];
};

/**
 * Compare two option arrays to see if they match
 */
const compareOptions = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;

  const norm = (arr) =>
    arr
      .map((o) => ({ id: String(o.id || ''), q: Number(o.quantity || 0) }))
      .sort((x, y) => (x.id > y.id ? 1 : x.id < y.id ? -1 : x.q - y.q));

  const A = norm(a);
  const B = norm(b);

  for (let i = 0; i < A.length; i++) {
    if (A[i].id !== B[i].id || A[i].q !== B[i].q) return false;
  }
  return true;
};

/**
 * Check if two cart items are the same entry (same variation ID and modifiers)
 */
export const isSameCartEntry = (cartItem, apiItem) => {
  if (!cartItem || !apiItem) return false;

  const cartVar =
    cartItem.variationId ||
    cartItem.variation_id ||
    cartItem.itemId ||
    cartItem.item_id ||
    '';
  const apiVar =
    apiItem.variationId ||
    apiItem.variation_id ||
    apiItem.itemId ||
    apiItem.item_id ||
    '';

  if (!cartVar || !apiVar) return false;
  if (String(cartVar) !== String(apiVar)) return false;

  const cartOpts = normalizeOptions(cartItem);
  const apiOpts = normalizeOptions(apiItem);

  // If both have no options, they match
  if (
    (cartOpts.length === 0 && apiOpts.length === 0) ||
    compareOptions(cartOpts, apiOpts)
  )
    return true;

  return false;
};

/**
 * Custom hook for cart item actions (quantity updates, item removal)
 * Extracted from CartDrawerCard for reuse in both CartDrawer and Cart page
 *
 * @param {Object} item - The cart item (lineItem from API)
 * @param {Object} options - Configuration options
 * @param {number} options.maxQuantity - Maximum allowed quantity (default: 10)
 * @param {Function} options.onUpdate - Optional callback when item is updated
 * @param {Function} options.onRemove - Optional callback when item is removed
 */
export const useCartItemActions = (item, options = {}) => {
  const { maxQuantity = 10, onUpdate, onRemove } = options;

  const dispatch = useDispatch();
  const user = useSelector((state) => state.authReducer.userData);
  // Use Redux cart state directly for consistency across components
  const reduxCart = useSelector((state) => state.shopReducer.cart);

  const [loading, setLoading] = useState(false);
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
  const [value, setValue] = useState(() => {
    const quantity = parseInt(item?.quantity);
    return !isNaN(quantity) ? quantity : 1;
  });

  // Debounce timer for Firebase updates
  const updateTimerRef = useRef(null);
  const pendingUpdateRef = useRef(null);
  const lastPriceRef = useRef(null); // Track price before update to detect actual changes

  // Sync local quantity with item prop (from API response)
  // Only update if the difference is significant to avoid flickering
  useEffect(() => {
    const safeQuantity = parseInt(item?.quantity);
    if (!isNaN(safeQuantity) && safeQuantity !== value && safeQuantity > 0) {
      // Use a small delay to batch updates and prevent flickering
      const timeoutId = setTimeout(() => {
        setValue(safeQuantity);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [item?.quantity]);

  // When item price changes (API response received), hide skeleton
  useEffect(() => {
    // Get price from various possible fields (handle both API format and transformed format)
    const grossSalesMoney = item?.grossSalesMoney
      ? parseFloat(item.grossSalesMoney)
      : 0;
    const calculatedPrice = item?.calculatedPrice
      ? parseFloat(item.calculatedPrice)
      : 0;
    const currentPrice = grossSalesMoney || calculatedPrice || 0;

    // Hide skeleton if:
    // 1. We're currently showing skeleton (isUpdatingPrice = true)
    // 2. We have a previous price to compare against (lastPriceRef.current !== null)
    // 3. The price has actually changed from the previous value (with small tolerance for floating point)
    if (
      isUpdatingPrice &&
      lastPriceRef.current !== null &&
      Math.abs(currentPrice - lastPriceRef.current) > 0.01
    ) {
      setIsUpdatingPrice(false);
      // Reset lastPriceRef after successful price update
      lastPriceRef.current = null;
    }
  }, [item?.grossSalesMoney, item?.calculatedPrice, isUpdatingPrice]);

  // Safety timeout: Reset isUpdatingPrice if it gets stuck (max 3 seconds)
  useEffect(() => {
    if (isUpdatingPrice) {
      const timeout = setTimeout(() => {
        setIsUpdatingPrice(false);
        lastPriceRef.current = null;
      }, 3000); // Reset after 3 seconds if still updating

      return () => clearTimeout(timeout);
    }
  }, [isUpdatingPrice]);

  // Cleanup Firebase timer on unmount
  useEffect(() => {
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, []);

  /**
   * Handle quantity change (increase or decrease)
   */
  const handleQuantityChange = useCallback(
    async (type, data) => {
      // Capture current price BEFORE starting the update
      // Handle both API format (grossSalesMoney as string/number) and transformed format
      const grossSalesMoney = item?.grossSalesMoney
        ? parseFloat(item.grossSalesMoney)
        : 0;
      const calculatedPrice = item?.calculatedPrice
        ? parseFloat(item.calculatedPrice)
        : 0;
      const currentPrice = grossSalesMoney || calculatedPrice || 0;
      lastPriceRef.current = currentPrice;

      try {
        const newQuantity = type === 'inc' ? value + 1 : value - 1;

        // Validate quantity bounds
        if (newQuantity < 1 || newQuantity > maxQuantity) {
          return;
        }

        // CRITICAL: Set loading state FIRST to show loader immediately
        setIsUpdatingPrice(true);
        
        // Update local state immediately for responsive UI
        setValue(newQuantity);

        // Use Redux cart as source of truth, fallback to user.bagItems for compatibility
        const currentCart = reduxCart && reduxCart.length > 0 ? reduxCart : (user?.bagItems || []);
        const bagItems = [...currentCart];
        const index = bagItems.findIndex((bagItem) =>
          isSameCartEntry(bagItem, data)
        );

        if (index === -1) {
          setIsUpdatingPrice(false);
          return;
        }

        // Update quantity (price will come from API)
        bagItems[index] = {
          ...bagItems[index],
          quantity: newQuantity,
        };

        // Batch Redux updates to prevent double re-renders
        // React 18+ automatically batches synchronous updates, but we use startTransition
        // for the user update to mark it as non-urgent, keeping UI responsive
        dispatch(setCart(bagItems));
        startTransition(() => {
          dispatch(setUser({ userData: { ...user, bagItems } }));
        });

        // Store pending update
        pendingUpdateRef.current = bagItems;

        // Clear existing Firebase timer
        if (updateTimerRef.current) {
          clearTimeout(updateTimerRef.current);
        }

        // CRITICAL: Update Firebase IMMEDIATELY before calling onUpdate
        // The API reads cart data from Firebase, so it must be updated first
        if (auth?.currentUser?.uid) {
          try {
            await firestore
              .collection('users')
              .doc(auth.currentUser.uid)
              .set({ bagItems }, { merge: true });
            pendingUpdateRef.current = null;
          } catch (firebaseError) {
            // Continue anyway - API might still work with Redux state
          }
        }

        // Also set up a debounced Firebase update for subsequent rapid changes
        // This ensures we don't spam Firebase if user changes quantity multiple times quickly
        updateTimerRef.current = setTimeout(() => {
          if (pendingUpdateRef.current && auth?.currentUser?.uid) {
            // Fire and forget - don't await to avoid blocking
            firestore
              .collection('users')
              .doc(auth.currentUser.uid)
              .set({ bagItems: pendingUpdateRef.current }, { merge: true })
              .then(() => {
                pendingUpdateRef.current = null;
              })
              .catch(() => {
                // Firebase error - silently fail, will retry on next update
              });
          }
        }, 500);

        // Call optional onUpdate callback AFTER Firebase is updated
        // This ensures API reads the correct cart data
        if (onUpdate) {
          onUpdate();
        }
      } catch (e) {
        setIsUpdatingPrice(false);
        // Revert on error
        setValue(item?.quantity || 1);
      }
    },
    [item, value, maxQuantity, user, reduxCart, dispatch, onUpdate]
  );

  /**
   * Handle item removal
   */
  const handleItemRemove = useCallback(
    async (card) => {
      try {
        setLoading(true);

        // Use Redux cart as source of truth, fallback to user.bagItems for compatibility
        const currentCart =
          reduxCart && reduxCart.length > 0 ? reduxCart : user?.bagItems || [];
        const bags = [...currentCart];
        const res = bags.findIndex((bagItem) => isSameCartEntry(bagItem, card));

        if (res === -1) {
          return;
        }

        bags.splice(res, 1);

        // Persist updated cart first, then update Redux so the spinner
        // is visible while the remove operation is in progress.
        if (auth?.currentUser) {
          try {
            await firestore
              .collection('users')
              .doc(auth.currentUser.uid)
              .set({ bagItems: bags }, { merge: true });
          } catch (firebaseError) {
            // If Firebase fails, still proceed with local Redux update
          }
        } else {
          // Update localStorage for non-authenticated users
          localStorage.setItem('cartItems', JSON.stringify(bags));
        }

        // Batch Redux updates to prevent double re-renders
        // React 18+ automatically batches synchronous updates
        dispatch(setCart(bags));
        startTransition(() => {
          dispatch(setUser({ userData: { ...user, bagItems: bags } }));
        });

        // Call optional onRemove callback
        if (onRemove) {
          onRemove();
        }
      } catch (e) {
        setLoading(false);
      }
    },
    [user, reduxCart, dispatch, onRemove]
  );

  return {
    quantity: value,
    loading,
    isUpdatingPrice,
    handleQuantityChange,
    handleItemRemove,
    // Export utility functions for external use if needed
    isSameCartEntry,
    normalizeOptions,
    compareOptions,
  };
};

export default useCartItemActions;

